<template>
  <div class="openmaps-container">
    <div class="map-container">
      <l-map ref="mapRef" :zoom="zoom" :center="mapCenter" :attributionControl="true" :maxZoom="19" :minZoom="1"
        style="height: 100%; width: 100%" @ready="onMapReady">
        <l-tile-layer :url="url" :attribution="attribution" :maxZoom="19"> </l-tile-layer>
        <div v-if="isLoadingBusStop" class="text-center my-4">
          <LoadingLoad :show="true" />
        </div>
      </l-map>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer } from 'vue3-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toRaw } from 'vue';
/* Ícones */
import IconBusStop from '@/assets/markerIcons/Icon mapa Ecra JCDCAUX-01.png';
import busIcon from '@/assets/veiculos/Autocarro.png';
import eletricoIcon from '@/assets/veiculos/Eletrico.png';
import madrugadaIcon from '@/assets/veiculos/Autocarro-madrugada.png';

/* Componente loading*/
import LoadingLoad from '@/components/generics/LoadingLoad.vue';
import { debugMode } from '@/config/config.js';
import { getEnvNumber } from '@/config/runtimeEnv';

export default {
  name: 'RealColourMap',

  components: {
    LMap,
    LTileLayer,
    LoadingLoad,
  },
  props: {
    busStopName: {
      type: String,
      required: false,
    },
    busPositions: {
      type: Array,
      default: () => [],
    },
    busStopLatitude: {
      type: Number,
      required: true,
    },
    busStopLongitude: {
      type: Number,
      required: true,
    },
    busStopNumber: {
      type: String,
      required: true,
    },
    routeColors: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      /* Zoom inicial — alterar este valor para mudar o zoom inicial do mapa */
      zoom: 16,
      url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
      attribution:
        '© <a href="https://osm.org/copyright" target="_blank">OpenStreetMap</a> contributors, ' +
        '<a href="https://www.hotosm.org/" target="_blank">HOT</a>,<a href="https://openstreetmap.fr/" target="_blank"> OSM France</a> ',
      mapObject: null,
      marker: null,
      busMarkers: [],
      isLoadingBusStop: false,
      resizeObserver: null,
    };
  },

  computed: {
    mapCenter() {
      if (
        typeof this.busStopLatitude === 'number' &&
        !isNaN(this.busStopLatitude) &&
        typeof this.busStopLongitude === 'number' &&
        !isNaN(this.busStopLongitude)
      ) {
        return [this.busStopLatitude, this.busStopLongitude];
      }
      if (debugMode) console.warn('Latitude ou Longitude não são números válidos:', this.busStopLatitude, this.busStopLongitude);
      return [getEnvNumber('VUE_APP_MAP_LAT', 38.736946), getEnvNumber('VUE_APP_MAP_LNG', -9.142685)];
    },

    flatBusPositions() {
      const flat = [];
      this.busPositions.forEach((route) => {
        if (Array.isArray(route.filteredPositions)) {
          const buses = route.filteredPositions.map((bus) => ({
            ...bus,
            routeNumber: route.routeNumber,
          }));
          flat.push(...buses);
        }
      });
      return flat;
    },
  },

  methods: {
    getVehicleIcon(routeNumber) {
      if (!routeNumber) return busIcon;
      if (routeNumber.toUpperCase().endsWith('E')) return eletricoIcon;
      if (routeNumber.startsWith('2')) return madrugadaIcon;
      return busIcon;
    },

    onMapReady(mapObject) {
      if (!mapObject) {
        console.error('O mapa não está disponível.');
        return;
      }

      this.mapObject = mapObject;

      // Força o zoom + centro via API do Leaflet (mais fiável que prop :zoom)
      this.mapObject.setView(this.mapCenter, this.zoom);

      this.addStopMarker();
      this.addBusMarkers();

      this.$nextTick(() => {
        setTimeout(() => {
          this.mapObject.invalidateSize();
          this.mapObject.setZoom(this.zoom);
        }, getEnvNumber('VUE_APP_MAP_TICK', 100));
      });
    },

    addStopMarker() {
      if (!this.mapObject || !this.mapCenter) {
        console.warn('O mapa ou o centro ainda não foi definido');
        return;
      }

      const stopIconWidth = 100;
      const stopIconHeight = 100;
      const stopIconAnchorX = 58;

      const stopIcon = L.icon({
        iconUrl: IconBusStop,
        iconSize: [stopIconWidth, stopIconHeight],
        // O ponto útil do PNG não está exatamente ao centro; este ajuste evita desvio visual no zoom.
        iconAnchor: [stopIconAnchorX, stopIconHeight],
        popupAnchor: [0, -stopIconHeight],
      });

      if (this.marker) {
        this.mapObject.removeLayer(this.marker);
      }

      this.marker = L.marker(this.mapCenter, { icon: stopIcon }).addTo(this.mapObject).bindPopup(`<div><strong>${this.busStopNumber}</strong></div>`);
    },

    addBusMarkers() {
      if (!this.mapObject || !this.routeColors || Object.keys(this.routeColors).length === 0) {
        console.warn('[addBusMarkers] Skipped:', {
          hasMapObject: !!this.mapObject,
          routeColorsCount: Object.keys(this.routeColors || {}).length,
        });
        return;
      }

      this.busMarkers.forEach((marker) => this.mapObject.removeLayer(marker));
      this.busMarkers = [];

      this.flatBusPositions.forEach((bus) => {
        const busIconDiv = this.getBusIcon(bus.routeNumber);
        const marker = L.marker([bus.lat, bus.lng], { icon: busIconDiv }).addTo(this.mapObject).bindPopup(`
                  <div>
                    <strong>Rota ${bus.routeNumber}</strong><br/>
                    Autocarro Nº: ${bus.BusNumber}<br/>
                  </div>
                `);
        this.busMarkers.push(marker);
      });
    },

    getBusIcon(routeNumber) {
      const color = this.routeColors?.[routeNumber] || '#808080';
      const textColor = color.toLowerCase() === '#ffffff' ? '#000000' : '#ffffff';
      const vehicleIcon = this.getVehicleIcon(routeNumber);

      return L.divIcon({
        html: `
      <div class="custom-bus-marker" style="position: relative; display: flex; align-items: center; justify-content: center;">

        <img src="${vehicleIcon}" alt="Bus Marker" class="bus-marker-icon" style="width:41px; height:41px;"/>

        <span class="bus-route-badge rounded-pill"
          style="
            position: absolute;
            top: 0;
            right: 0;
            transform: translate(80%, -50%);
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${color};
            color: ${textColor};
            padding: 4px 8px;
            font-size: 20px;
            font-weight: bold;
            line-height: 1;
            height: auto;
            min-width: 32px;
            text-align: center;
            border-radius: 12px;
          "
        >${routeNumber}</span>
      </div>
    `,
        className: 'bus-div-icon',
      });
    },
  },

  watch: {
    mapCenter(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        if (this.mapObject) {
          this.mapObject.setView(newVal, this.zoom);
        }
        this.addStopMarker();
      }
    },

    zoom(newVal) {
      if (this.mapObject) {
        this.mapObject.setZoom(newVal);
      }
    },

    busPositions(newVal) {
      this.addBusMarkers();
      if (debugMode) console.log('Actualização das posições dos autocarros:', toRaw(newVal));
      toRaw(newVal);
    },

    routeColors: {
      handler(newVal) {
        if (!newVal || Object.keys(newVal).length === 0) {
          console.warn('routeColors está vazio ou indefinido no RealColourMap');
          return;
        }
        this.addBusMarkers();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Espera que o Vue termine de renderizar o template
    this.$nextTick(() => {
      // Pega a referência ao elemento HTML do mapa
      const mapContainer = this.$refs.mapRef?.$el;

      // Verifica se o container existe e se o browser suporta ResizeObserver
      if (mapContainer && window.ResizeObserver) {
        // Cria um "observador" que vigia mudanças de tamanho do container
        this.resizeObserver = new ResizeObserver(() => {
          // Sempre que o container mudar de tamanho, chama invalidateSize
          if (this.mapObject) {
            this.mapObject.invalidateSize();
          }
        });

        // Começa a observar o container do mapa
        this.resizeObserver.observe(mapContainer);
      }
      //  Força um invalidateSize manual depois do mapa estar montado
      // Isto resolve casos onde o CSS já está aplicado antes do observer começar
      setTimeout(() => {
        if (this.mapObject) {
          this.mapObject.invalidateSize();
        }
      }, 500);

      // Segundo timeout de segurança (caso o mapa demore a carregar tiles)
      setTimeout(() => {
        if (this.mapObject) {
          this.mapObject.invalidateSize();
          this.mapObject.setView(this.mapCenter, this.zoom);
        }
      }, 1000);
    });
  },
  beforeUnmount() {
    // Quando o componente for destruído, desliga o observador
    // Isto evita memory leaks (o observador a tentar vigiar algo que já não existe)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
};
</script>

<style>
.openmaps-container {
  height: 854px;
  width: 960px;
}

.map-container {
  height: 854px;
  width: 960px;
}

.map-container .leaflet-container {
  width: 960px !important;
  height: 854px !important;
}

/* .marker-badge {
  position: absolute;
  top: 50%;
  left: -350%;
  background-color: #fbd700;
  color: black;
  border: 2px solid black;
  border-radius: 50%;
  width: 40px;
  text-align: center;
  font-size: 10px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  transform: translateY(-50%);
} */

/* Ícones do autocarro */
.custom-bus-marker {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bus-marker-icon {
  background: none;
  border: none;
}

/* Badge do número da rota */
.bus-route-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.6px;
  text-align: center;
  padding: 1px 4px;
  min-width: 18px;
}
</style>
